// Audio Manager using Web Audio API with preloaded buffers for better performance on iOS/Mac
import diceAudioSrc from "@/audio/dice-sound.mp3"
import moneyAnimationAudioSrc from "@/audio/money-animation.wav"
import pieceMovementAudioSrc from "@/audio/piece-movement.mp3"
import cardDrawAudioSrc from "@/audio/card-draw.mp3"
import policeAudioSrc from "@/audio/police.mp3"
import jailDropAudioSrc from "@/audio/jail-drop.mp3"
import propertyCardMovementAudioSrc from "@/audio/property-card-movement.mp3"
import propertySoldAudioSrc from "@/audio/property-sold.mp3"
import auctionWonAudioSrc from "@/audio/auction-won.mp3"
import turnStartedAudioSrc from "@/audio/turn-started.mp3"
import lightTapAudioSrc from "@/audio/light-tap.mp3"
import lightTap2AudioSrc from "@/audio/light-tap2.mp3"
import mouseClickAudioSrc from "@/audio/mouse-click.mp3"
import sliderTickAudioSrc from "@/audio/slider-tick.mp3"
import auctionPlacedAudioSrc from "@/audio/auction-placed.mp3"
import smallErrorAudioSrc from "@/audio/small-error.mp3"
import tradeOfferedAudioSrc from "@/audio/trade-offered.mp3"
import tickTockAudioSrc from "@/audio/tick-tock.mp3"
import {
  MUSIC_VOLUME_CHANGED_EVENT,
  SOUND_EFFECTS_VOLUME_CHANGED_EVENT,
  TIMER_VOLUME_CHANGED_EVENT,
  clampVolume,
  getMusicVolume,
  getSoundEffectsVolume,
  getTimerVolume,
} from "@/utils/volumePreference"
import {
  AUTO_MUSIC_TRACK,
  MUSIC_TRACK_CHANGED_EVENT,
  MusicTrackSelection,
  getMusicTrackSelection,
  getMusicTrackSrc,
  getNextMusicTrackSrc,
  musicTracks,
} from "@/utils/musicTrackPreference"
// The level the background tune plays at when the music slider is at 100%.
// Deliberately no attenuation: the tune used to be pinned at 2% here, which put
// its ceiling below what anyone could hear. Quietening it is the slider's job
// now, not this constant's.
const BACKGROUND_MUSIC_VOLUME = 1
// Tunes are faded in and out rather than cut, so swapping track (or muting)
// mid-bar doesn't pop. Short enough to still feel immediate.
const BACKGROUND_MUSIC_FADE = 0.18

// The level the clock tick loops at when its slider is at 100%. Kept below full
// because it plays underneath everything else for the whole length of a timer.
const TIMER_TICK_VOLUME = 0.8
// Short fades on the tick loop: it is started and stopped mid-file every time a
// timer opens or closes, and a hard cut on a waveform that is rarely at zero
// clicks.
const TIMER_TICK_FADE = 0.12

class AudioManager {
  private audioContext: AudioContext | null = null
  private buffers: Map<string, AudioBuffer> = new Map()
  private isInitialized = false
  private initializationPromise: Promise<void> | null = null
  private maxConcurrentSources = 5 // Maximum concurrent audio sources per sound type
  private backgroundSource: AudioBufferSourceNode | null = null
  private backgroundGainNode: GainNode | null = null
  private isBackgroundMusicPlaying = false
  // User-set mixes (0..1). Every call-site volume is a level *within* its mix,
  // so these scale on top of whatever a caller asks for.
  private musicVolume = getMusicVolume()
  private effectsVolume = getSoundEffectsVolume()
  private timerVolume = getTimerVolume()
  // The level the currently playing tune was started at, kept so the slider can
  // re-scale it without restarting playback.
  private backgroundBaseVolume = BACKGROUND_MUSIC_VOLUME
  // Which tune to play: a track id, or "auto" for the rotation.
  private musicTrackSelection: MusicTrackSelection = getMusicTrackSelection()
  // The tune that is (or was last) playing. Kept after a stop so switching the
  // music back on in auto mode carries on from where the rotation was rather
  // than starting the first track again.
  private currentTrackSrc: string | null = null
  // Bumped by every stop and every track switch, so a start that is still
  // waiting on its buffer — these files are megabytes — can tell it has been
  // superseded, and so an `onended` can tell a real end of track from a stop.
  private backgroundToken = 0
  // The looping clock tick, held separately from the tune so the two can play
  // over each other and be mixed independently.
  private tickSource: AudioBufferSourceNode | null = null
  private tickGainNode: GainNode | null = null
  private isTickPlaying = false
  // Bumped by every stop, so a start that is still waiting on the buffer can
  // tell it has been cancelled and not begin playing after the fact.
  private tickToken = 0

  constructor() {
    if (typeof window === "undefined") return
    window.addEventListener(MUSIC_VOLUME_CHANGED_EVENT, (event) => {
      this.setMusicVolume((event as CustomEvent<number>).detail)
    })
    window.addEventListener(SOUND_EFFECTS_VOLUME_CHANGED_EVENT, (event) => {
      this.effectsVolume = clampVolume((event as CustomEvent<number>).detail)
    })
    window.addEventListener(TIMER_VOLUME_CHANGED_EVENT, (event) => {
      this.setTimerVolume((event as CustomEvent<number>).detail)
    })
    window.addEventListener(MUSIC_TRACK_CHANGED_EVENT, (event) => {
      this.setMusicTrack((event as CustomEvent<MusicTrackSelection>).detail)
    })
  }

  // Applied to the tune that is already looping, so dragging the slider is
  // audible straight away instead of only on the next game.
  setMusicVolume(volume: number): void {
    this.musicVolume = clampVolume(volume)
    if (!this.backgroundGainNode) return
    const target = this.backgroundBaseVolume * this.musicVolume
    const ctx = this.audioContext
    if (!ctx) {
      this.backgroundGainNode.gain.value = target
      return
    }
    // Ramped rather than set, so a drag doesn't crackle.
    const now = ctx.currentTime
    const gain = this.backgroundGainNode.gain
    gain.cancelScheduledValues(now)
    gain.setValueAtTime(gain.value, now)
    gain.linearRampToValueAtTime(target, now + 0.08)
  }

  setEffectsVolume(volume: number): void {
    this.effectsVolume = clampVolume(volume)
  }

  getEffectsVolume(): number {
    return this.effectsVolume
  }

  // Like the music slider, applied to the loop that is already running so the
  // change is audible during the timer you are sitting on, not the next one.
  setTimerVolume(volume: number): void {
    this.timerVolume = clampVolume(volume)
    if (!this.tickGainNode) return
    const target = TIMER_TICK_VOLUME * this.timerVolume
    const ctx = this.audioContext
    if (!ctx) {
      this.tickGainNode.gain.value = target
      return
    }
    const now = ctx.currentTime
    const gain = this.tickGainNode.gain
    gain.cancelScheduledValues(now)
    gain.setValueAtTime(gain.value, now)
    gain.linearRampToValueAtTime(target, now + 0.08)
  }

  getTimerVolume(): number {
    return this.timerVolume
  }

  // Initialize Web Audio API context (must be called after user interaction on some browsers)
  private async initializeContext(): Promise<void> {
    if (this.audioContext) return

    try {
      // Create AudioContext (with fallback for older browsers)
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext
      this.audioContext = new AudioContextClass()

      // Resume context if suspended (required on some browsers after user interaction)
      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume()
      }
    } catch (error) {
      console.error("Failed to initialize AudioContext:", error)
      throw error
    }
  }

  // Preload all audio files as buffers
  async preloadAll(): Promise<void> {
    if (this.isInitialized) return
    if (this.initializationPromise) return this.initializationPromise

    this.initializationPromise = (async () => {
      try {
        await this.initializeContext()
        if (!this.audioContext) throw new Error("AudioContext not initialized")

        const audioFiles = [
          diceAudioSrc,
          moneyAnimationAudioSrc,
          pieceMovementAudioSrc,
          cardDrawAudioSrc,
          policeAudioSrc,
          jailDropAudioSrc,
          propertyCardMovementAudioSrc,
          propertySoldAudioSrc,
          auctionWonAudioSrc,
          turnStartedAudioSrc,
          lightTapAudioSrc,
          lightTap2AudioSrc,
          mouseClickAudioSrc,
          sliderTickAudioSrc,
          auctionPlacedAudioSrc,
          // The tunes are megabytes each and are not in this list on purpose:
          // they are fetched by startBackgroundMusic when one is actually
          // needed, so a multi-MB download can't hold up the effects (this
          // preload gates the app's asset loading).
          smallErrorAudioSrc,
          tradeOfferedAudioSrc,
          tickTockAudioSrc,
        ]

        // Load all audio files in parallel
        await Promise.all(
          audioFiles.map(async (src) => {
            try {
              const buffer = await this.loadAudioBuffer(src)
              this.buffers.set(src, buffer)
            } catch (error) {
              console.error(`Failed to load audio: ${src}`, error)
            }
          })
        )

        this.isInitialized = true
      } catch (error) {
        console.error("Failed to preload audio:", error)
        throw error
      }
    })()

    return this.initializationPromise
  }

  // Load audio file and decode to buffer
  private async loadAudioBuffer(audioSrc: string): Promise<AudioBuffer> {
    if (!this.audioContext) {
      await this.initializeContext()
      if (!this.audioContext) throw new Error("AudioContext not available")
    }

    // Check if already loaded
    if (this.buffers.has(audioSrc)) {
      return this.buffers.get(audioSrc)!
    }

    try {
      const response = await fetch(audioSrc)
      if (!response.ok) {
        throw new Error(`Failed to fetch audio: ${response.statusText}`)
      }

      const arrayBuffer = await response.arrayBuffer()
      const audioBuffer = await this.audioContext!.decodeAudioData(arrayBuffer)
      this.buffers.set(audioSrc, audioBuffer)
      return audioBuffer
    } catch (error) {
      console.error(`Error loading audio buffer for ${audioSrc}:`, error)
      throw error
    }
  }

  // Play audio using preloaded buffer (non-blocking, returns immediately)
  async play(audioSrc: string, volume = 1): Promise<void> {
    try {
      // Silenced effects cost nothing: don't even wake the context up.
      const effectiveVolume = volume * this.effectsVolume
      if (effectiveVolume <= 0) return

      // Ensure context is initialized
      if (!this.audioContext) {
        await this.initializeContext()
      }

      // Ensure buffer is loaded
      if (!this.buffers.has(audioSrc)) {
        await this.loadAudioBuffer(audioSrc)
      }

      const buffer = this.buffers.get(audioSrc)
      if (!buffer || !this.audioContext) {
        console.warn(`Audio buffer not available for: ${audioSrc}`)
        return
      }

      // Resume context if suspended (required after user interaction on some browsers)
      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume()
      }

      // Create and play audio source
      const source = this.audioContext.createBufferSource()
      source.buffer = buffer
      const gainNode = this.audioContext.createGain()
      gainNode.gain.value = effectiveVolume
      source.connect(gainNode)
      gainNode.connect(this.audioContext.destination)

      // Handle source completion
      source.onended = () => {
        source.disconnect()
        gainNode.disconnect()
      }

      // Start playback immediately (no decoding delay)
      source.start(0)
    } catch (error) {
      // Silently fail to avoid breaking animations
      console.warn(`Failed to play audio: ${audioSrc}`, error)
    }
  }

  /**
   * Start the background music on whichever tune the current selection asks
   * for. Already playing is a no-op, so the several places that call this (the
   * music hook on mount, the first user gesture, the note button) don't restart
   * the tune on top of itself.
   */
  async startBackgroundMusic(volume = BACKGROUND_MUSIC_VOLUME): Promise<void> {
    if (this.isBackgroundMusicPlaying) return
    await this.playBackgroundTrack(this.resolveStartTrackSrc(), volume)
  }

  /**
   * Change which tune plays, live.
   *
   * Picking a track while another one is playing swaps it there and then —
   * that is the point of the picker. Switching to *auto* deliberately does not:
   * the tune you are on stops looping and the rotation takes over from the end
   * of it, which is far less jarring than cutting off mid-bar.
   */
  setMusicTrack(selection: MusicTrackSelection): void {
    this.musicTrackSelection = selection

    // Music is off: stored only, and startBackgroundMusic will honour it.
    // `backgroundSource` may still be null here while a tune is downloading —
    // the cases below are written to cope, since these files take a moment.
    if (!this.isBackgroundMusicPlaying) return

    if (selection === AUTO_MUSIC_TRACK) {
      if (this.backgroundSource) this.backgroundSource.loop = false
      // The tune after this one may not be downloaded yet.
      this.prefetchNextTrack(this.currentTrackSrc)
      return
    }

    const src = getMusicTrackSrc(selection)
    if (!src) return
    if (src === this.currentTrackSrc) {
      // Already on it — just stop it handing over to the next one.
      if (this.backgroundSource) this.backgroundSource.loop = true
      return
    }
    void this.playBackgroundTrack(src, this.backgroundBaseVolume)
  }

  /** The tune a fresh start should play. */
  private resolveStartTrackSrc(): string {
    if (this.musicTrackSelection !== AUTO_MUSIC_TRACK) {
      return getMusicTrackSrc(this.musicTrackSelection) ?? musicTracks[0].src
    }
    // Auto: carry on with the rotation where it left off.
    return this.currentTrackSrc ?? musicTracks[0].src
  }

  /**
   * Play one tune, replacing whatever is playing. A single track loops itself;
   * in auto mode it runs once and hands over to the next one when it ends.
   */
  private async playBackgroundTrack(
    audioSrc: string,
    volume = BACKGROUND_MUSIC_VOLUME
  ): Promise<void> {
    const token = ++this.backgroundToken
    try {
      this.fadeOutBackgroundSource()
      // Claimed before the awaits below, so the calls that fire on mount and on
      // the first gesture can't both end up starting a tune.
      this.isBackgroundMusicPlaying = true
      this.currentTrackSrc = audioSrc

      if (!this.audioContext) {
        await this.initializeContext()
      }

      if (!this.buffers.has(audioSrc)) {
        await this.loadAudioBuffer(audioSrc)
      }
      // Stopped, or switched to another tune, while the file was downloading.
      if (token !== this.backgroundToken) return

      const buffer = this.buffers.get(audioSrc)
      if (!buffer || !this.audioContext) {
        console.warn(`Background music buffer not available for: ${audioSrc}`)
        this.isBackgroundMusicPlaying = false
        return
      }

      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume()
      }
      if (token !== this.backgroundToken) return

      const source = this.audioContext.createBufferSource()
      source.buffer = buffer
      source.loop = this.musicTrackSelection !== AUTO_MUSIC_TRACK

      const gainNode = this.audioContext.createGain()
      this.backgroundBaseVolume = volume
      const target = volume * this.musicVolume
      const now = this.audioContext.currentTime
      gainNode.gain.setValueAtTime(0, now)
      gainNode.gain.linearRampToValueAtTime(target, now + BACKGROUND_MUSIC_FADE)
      source.connect(gainNode)
      gainNode.connect(this.audioContext.destination)

      source.onended = () => {
        source.disconnect()
        gainNode.disconnect()
        // A stop or a track switch already dealt with the bookkeeping — and
        // must not be mistaken for the tune reaching its end.
        if (token !== this.backgroundToken) return
        this.backgroundSource = null
        this.backgroundGainNode = null
        this.isBackgroundMusicPlaying = false
        if (this.musicTrackSelection === AUTO_MUSIC_TRACK) {
          void this.playBackgroundTrack(
            getNextMusicTrackSrc(audioSrc),
            this.backgroundBaseVolume
          )
        }
      }

      source.start(0)
      this.backgroundSource = source
      this.backgroundGainNode = gainNode
      // Downloading the next tune during the hand-over would be a silence of
      // several seconds, so it is fetched while this one plays.
      if (this.musicTrackSelection === AUTO_MUSIC_TRACK) {
        this.prefetchNextTrack(audioSrc)
      }
    } catch (error) {
      console.warn(`Failed to start background music: ${audioSrc}`, error)
      if (token === this.backgroundToken) this.isBackgroundMusicPlaying = false
    }
  }

  private prefetchNextTrack(src: string | null): void {
    const next = getNextMusicTrackSrc(src)
    if (this.buffers.has(next)) return
    void this.loadAudioBuffer(next).catch(() => {})
  }

  /**
   * Fade the running tune down and let it go. The caller is expected to have
   * bumped `backgroundToken` first, so the source's own `onended` disconnects it
   * without treating the stop as the track finishing.
   */
  private fadeOutBackgroundSource(): void {
    const source = this.backgroundSource
    const gainNode = this.backgroundGainNode
    this.backgroundSource = null
    this.backgroundGainNode = null
    if (!source) return

    const ctx = this.audioContext
    if (!ctx || !gainNode) {
      source.stop()
      source.disconnect()
      gainNode?.disconnect()
      return
    }

    const now = ctx.currentTime
    gainNode.gain.cancelScheduledValues(now)
    gainNode.gain.setValueAtTime(gainNode.gain.value, now)
    gainNode.gain.linearRampToValueAtTime(0, now + BACKGROUND_MUSIC_FADE)
    source.stop(now + BACKGROUND_MUSIC_FADE)
  }

  /**
   * Start the clock ticking. The asset is a few seconds of ticking, so it loops
   * for as long as the timer is up.
   *
   * Calling this while it is already ticking is a no-op — one timer handing over
   * to another (buy-or-auction into the auction itself, say) keeps one unbroken
   * tick rather than restarting the loop.
   */
  async startTimerTick(): Promise<void> {
    try {
      if (this.isTickPlaying) return
      const token = ++this.tickToken

      if (!this.audioContext) {
        await this.initializeContext()
      }

      if (!this.buffers.has(tickTockAudioSrc)) {
        await this.loadAudioBuffer(tickTockAudioSrc)
      }

      // The timer this was started for is long gone (or another start won the
      // race) by the time the buffer landed.
      if (token !== this.tickToken || this.isTickPlaying) return

      const buffer = this.buffers.get(tickTockAudioSrc)
      if (!buffer || !this.audioContext) {
        console.warn(`Timer tick buffer not available for: ${tickTockAudioSrc}`)
        return
      }

      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume()
      }
      if (token !== this.tickToken || this.isTickPlaying) return

      const source = this.audioContext.createBufferSource()
      source.buffer = buffer
      source.loop = true

      const gainNode = this.audioContext.createGain()
      const target = TIMER_TICK_VOLUME * this.timerVolume
      const now = this.audioContext.currentTime
      gainNode.gain.setValueAtTime(0, now)
      gainNode.gain.linearRampToValueAtTime(target, now + TIMER_TICK_FADE)
      source.connect(gainNode)
      gainNode.connect(this.audioContext.destination)

      source.onended = () => {
        if (this.tickSource === source) {
          this.tickSource = null
          this.tickGainNode = null
          this.isTickPlaying = false
        }
        source.disconnect()
        gainNode.disconnect()
      }

      source.start(0)
      this.tickSource = source
      this.tickGainNode = gainNode
      this.isTickPlaying = true
    } catch (error) {
      console.warn(`Failed to start timer tick: ${tickTockAudioSrc}`, error)
    }
  }

  stopTimerTick(): void {
    try {
      // Cancels any start still waiting on its buffer, so a timer that closed
      // before the sound was ready never begins ticking.
      this.tickToken++
      const source = this.tickSource
      const gainNode = this.tickGainNode
      this.tickSource = null
      this.tickGainNode = null
      this.isTickPlaying = false
      if (!source) return

      const ctx = this.audioContext
      if (!ctx) {
        source.stop()
        source.disconnect()
        gainNode?.disconnect()
        return
      }

      // Faded out rather than cut, then stopped once the fade has run.
      const now = ctx.currentTime
      if (gainNode) {
        gainNode.gain.cancelScheduledValues(now)
        gainNode.gain.setValueAtTime(gainNode.gain.value, now)
        gainNode.gain.linearRampToValueAtTime(0, now + TIMER_TICK_FADE)
      }
      source.stop(now + TIMER_TICK_FADE)
    } catch (error) {
      console.warn("Failed to stop timer tick", error)
    }
  }

  /**
   * A short sample of the tick at the current timer level, for the settings
   * slider. Independent of the looping tick, so previewing while a real timer is
   * running doesn't interrupt it.
   */
  async playTimerTickPreview(durationSec = 1.6): Promise<void> {
    try {
      const target = TIMER_TICK_VOLUME * this.timerVolume
      if (target <= 0) return

      if (!this.audioContext) {
        await this.initializeContext()
      }
      if (!this.buffers.has(tickTockAudioSrc)) {
        await this.loadAudioBuffer(tickTockAudioSrc)
      }
      const buffer = this.buffers.get(tickTockAudioSrc)
      const ctx = this.audioContext
      if (!buffer || !ctx) return
      if (ctx.state === "suspended") {
        await ctx.resume()
      }

      const source = ctx.createBufferSource()
      source.buffer = buffer
      const gainNode = ctx.createGain()
      const now = ctx.currentTime
      gainNode.gain.setValueAtTime(0, now)
      gainNode.gain.linearRampToValueAtTime(target, now + TIMER_TICK_FADE)
      gainNode.gain.setValueAtTime(target, now + durationSec)
      gainNode.gain.linearRampToValueAtTime(0, now + durationSec + TIMER_TICK_FADE)
      source.connect(gainNode)
      gainNode.connect(ctx.destination)
      source.onended = () => {
        source.disconnect()
        gainNode.disconnect()
      }
      source.start(0)
      source.stop(now + durationSec + TIMER_TICK_FADE)
    } catch (error) {
      console.warn("Failed to play timer tick preview", error)
    }
  }

  stopBackgroundMusic(): void {
    try {
      // Cancels any start still waiting on its buffer, and tells the running
      // source's `onended` that this was a stop rather than the end of a track
      // (which in auto mode would start the next one).
      this.backgroundToken++
      this.fadeOutBackgroundSource()
      this.isBackgroundMusicPlaying = false
      // `currentTrackSrc` is deliberately left alone: switching the music back
      // on resumes the rotation from here.
    } catch (error) {
      console.warn("Failed to stop background music", error)
    }
  }

  // Synthesize a short "energy strike" sound (descending zap + impact thud)
  // entirely with the Web Audio API, so no extra audio asset is needed. Used by
  // the striking-beam card animation.
  async playBeamStrike(volume = 0.5): Promise<void> {
    try {
      const effectiveVolume = volume * this.effectsVolume
      if (effectiveVolume <= 0) return

      if (!this.audioContext) {
        await this.initializeContext()
      }
      const ctx = this.audioContext
      if (!ctx) return
      if (ctx.state === "suspended") {
        await ctx.resume()
      }

      const now = ctx.currentTime

      const master = ctx.createGain()
      master.gain.value = effectiveVolume
      master.connect(ctx.destination)

      // 1) Charge/launch: a fast downward frequency sweep ("pew").
      const zap = ctx.createOscillator()
      zap.type = "sawtooth"
      zap.frequency.setValueAtTime(1400, now)
      zap.frequency.exponentialRampToValueAtTime(180, now + 0.28)
      const zapGain = ctx.createGain()
      zapGain.gain.setValueAtTime(0.0001, now)
      zapGain.gain.exponentialRampToValueAtTime(0.9, now + 0.02)
      zapGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.3)
      zap.connect(zapGain)
      zapGain.connect(master)
      zap.start(now)
      zap.stop(now + 0.32)

      // 2) Impact: a short filtered noise burst timed to when the beam lands.
      const impactAt = now + 0.28
      const noiseBuffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.2), ctx.sampleRate)
      const noiseData = noiseBuffer.getChannelData(0)
      for (let i = 0; i < noiseData.length; i++) {
        noiseData[i] = (Math.random() * 2 - 1) * (1 - i / noiseData.length)
      }
      const noise = ctx.createBufferSource()
      noise.buffer = noiseBuffer
      const noiseFilter = ctx.createBiquadFilter()
      noiseFilter.type = "lowpass"
      noiseFilter.frequency.setValueAtTime(2200, impactAt)
      noiseFilter.frequency.exponentialRampToValueAtTime(400, impactAt + 0.18)
      const noiseGain = ctx.createGain()
      noiseGain.gain.setValueAtTime(0.0001, impactAt)
      noiseGain.gain.exponentialRampToValueAtTime(0.7, impactAt + 0.01)
      noiseGain.gain.exponentialRampToValueAtTime(0.0001, impactAt + 0.22)
      noise.connect(noiseFilter)
      noiseFilter.connect(noiseGain)
      noiseGain.connect(master)
      noise.start(impactAt)
      noise.stop(impactAt + 0.24)

      // 3) Low-end "thump" that gives the impact real body/weight.
      const thump = ctx.createOscillator()
      thump.type = "sine"
      thump.frequency.setValueAtTime(140, impactAt)
      thump.frequency.exponentialRampToValueAtTime(46, impactAt + 0.2)
      const thumpGain = ctx.createGain()
      thumpGain.gain.setValueAtTime(0.0001, impactAt)
      thumpGain.gain.exponentialRampToValueAtTime(0.95, impactAt + 0.016)
      thumpGain.gain.exponentialRampToValueAtTime(0.0001, impactAt + 0.28)
      thump.connect(thumpGain)
      thumpGain.connect(master)
      thump.start(impactAt)
      thump.stop(impactAt + 0.32)

      // Disconnect the master node once everything has rung out.
      window.setTimeout(() => {
        try {
          master.disconnect()
        } catch {}
      }, 900)
    } catch (error) {
      console.warn("Failed to play beam strike sound", error)
    }
  }

  // Clean up all audio resources
  cleanup(): void {
    this.stopBackgroundMusic()
    this.stopTimerTick()
    this.buffers.clear()
    if (this.audioContext) {
      this.audioContext.close().catch(console.error)
      this.audioContext = null
    }
    this.isInitialized = false
    this.initializationPromise = null
  }
}

// Create singleton instance
export const audioManager = new AudioManager()

/**
 * For the effects that are still played through a plain `<audio>` element
 * (the gsap animation callbacks, mostly) rather than a preloaded buffer. Use it
 * instead of `new Audio(src)` so those sounds obey the effects slider too — the
 * level is read when the element is built, which is right before it plays.
 */
export const createEffectAudio = (
  audioSrc: string,
  volume = 1
): HTMLAudioElement => {
  const audio = new Audio(audioSrc)
  audio.volume = Math.min(Math.max(volume * audioManager.getEffectsVolume(), 0), 1)
  return audio
}

// Export convenience functions for common audio files
export const playDiceSound = () => audioManager.play(diceAudioSrc)
export const playMoneyAnimationSound = () => audioManager.play(moneyAnimationAudioSrc)
export const playPieceMovementSound = () => audioManager.play(pieceMovementAudioSrc)
export const playCardDrawSound = () => audioManager.play(cardDrawAudioSrc)
export const playPoliceSound = () => audioManager.play(policeAudioSrc)
export const playJailDropSound = () => audioManager.play(jailDropAudioSrc)
export const playPropertyCardMovementSound = () => audioManager.play(propertyCardMovementAudioSrc)
export const playPropertySoldSound = () => audioManager.play(propertySoldAudioSrc)
export const playAuctionWonSound = () => audioManager.play(auctionWonAudioSrc)
export const playTurnStartedSound = () => audioManager.play(turnStartedAudioSrc)
export const playLightTapSound = () => audioManager.play(lightTapAudioSrc)
export const playLightTap2Sound = () => audioManager.play(lightTap2AudioSrc)
export const playMouseClickSound = () => audioManager.play(mouseClickAudioSrc)
export const playSliderTickSound = () => audioManager.play(sliderTickAudioSrc, 0.4)
export const playSliderToggle = playSliderTickSound
export const playAuctionPlacedSound = () => audioManager.play(auctionPlacedAudioSrc)
export const startBackgroundMusic = () => audioManager.startBackgroundMusic(BACKGROUND_MUSIC_VOLUME)
export const stopBackgroundMusic = () => audioManager.stopBackgroundMusic()
// Which tune plays is changed through setMusicTrackSelection
// (utils/musicTrackPreference): it stores the choice and announces it, and the
// manager above picks it up and switches the music that is already playing.
export const playSmallErrorSound = () => audioManager.play(smallErrorAudioSrc)

export const playTradeOfferedSound = () => audioManager.play(tradeOfferedAudioSrc)

// The clock tick that runs while you are on a timer. Drive these through
// useTimerTickSound rather than calling them directly, so overlapping timers
// share one loop.
export const startTimerTickSound = () => audioManager.startTimerTick()
export const stopTimerTickSound = () => audioManager.stopTimerTick()
export const previewTimerTickSound = () => audioManager.playTimerTickPreview()

export const playStrikeBeamSound = () => audioManager.playBeamStrike()
